import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, JsonPipe, SlicePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { BaseRules, TextValues } from './new-jersey.model';
import { PipBaseStateComponent } from '../pip-base-state.component';
import {
  AdditionalOptions,
  CoverageLimit,
  PersonalInjuryProtectionSelection,
} from '../../../config/pip-constants';
import { TruncateModule } from 'axis-components/truncate';

@Component({
  selector: 'app-new-jersey',
  templateUrl: './new-jersey.component.html',
  styleUrls: ['./new-jersey.component.scss'],
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    DropdownModule,
    CommonModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    JsonPipe,
    TruncateModule,
    TooltipModule,
  ],
})
export class NewJerseyComponent extends PipBaseStateComponent<
  BaseRules,
  TextValues
> {
  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('new-jersey', form);
    }
  }

  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('new-jersey', null);
    }
  }

  optionsSelection = {
    Income_Continuation: '',
    Essential_Services: '',
    Added_Death_Benefit: '',
    Funeral_Benefit: '',
  };

  override eventHook(value: any): void {
    if (value === 'pipOptions_DropDown') {
      this.optionsSelection.Income_Continuation = '';
      this.optionsSelection.Essential_Services = '';
      this.optionsSelection.Added_Death_Benefit = '';
      this.optionsSelection.Funeral_Benefit = '';
    }

    if (value === 'trucksOnly_TrucksLevelOne_Radio') {
      // Hide extendedMedicalExpenseBenefits_Checkbox when trucksOnly is selected
      if (this.baseRules) {
        this.baseRules = {
          ...this.baseRules,
          extendedMedicalExpenseBenefits_Checkbox: 'hide',
        };
      }

      // Set extendedMedicalExpenseBenefits_Checkbox value to false
      const extendedMedical = this.reactiveForm?.get(
        'extendedMedicalExpenseBenefits_Checkbox',
      );
      if (extendedMedical) {
        extendedMedical.setValue(false);
        extendedMedical.updateValueAndValidity();
      }
    }
  }
  override setupHookForDropDownData(): void {
    if (!this.baseRules) {
      return;
    }

    const trucksControl = this.reactiveForm?.get('trucksLevelOne_Radio');
    const extendedMedical = this.reactiveForm?.get(
      'extendedMedicalExpenseBenefits_Checkbox',
    );

    const hasExistingTrucksSelection =
      trucksControl?.value !== null &&
      trucksControl?.value !== undefined &&
      trucksControl?.value !== '';
    const hasExistingExtendedMedicalSelection =
      extendedMedical?.value !== null &&
      extendedMedical?.value !== undefined &&
      extendedMedical?.value !== '';

    // Handle New Jersey ShowMaxOptions case
    if (
      this.baseRules['selectionId'] ===
        'AcceptCoverage_RejectAdditionalPIP_MaximumLimitsAvailable' ||
      this.baseRules['selectionId'] ===
        'AcceptCoverage_VaryByState_MaximumLimitsAvailable' ||
      this.baseRules['selectionId'] ===
        'AcceptCoverage_AcceptAdditionalPIP_MaximumLimitsAvailable'
    ) {
      if (trucksControl && !hasExistingTrucksSelection) {
        trucksControl.setValue('noTrucks_TrucksLevelOne_Radio');
        trucksControl.updateValueAndValidity();
      }

      const selectedTruckMode = trucksControl?.value;
      if (selectedTruckMode === 'noTrucks_TrucksLevelOne_Radio') {
        this.baseRules = {
          ...this.baseRules,
          extendedMedicalExpenseBenefits_Checkbox: 'show',
          show_readonly: true,
          show_label: false,
        };
      }

      // Apply default checkbox selection only when the user has no prior value.
      if (
        extendedMedical &&
        selectedTruckMode === 'noTrucks_TrucksLevelOne_Radio' &&
        !hasExistingExtendedMedicalSelection
      ) {
        extendedMedical.setValue(true);
        extendedMedical.updateValueAndValidity();
      }
    }
  }

  override dynamicRuleQueryBuilder(): string {
    //Condition only for Reject where permissible
    if (
      this.baseRulesService.personalInjuryProtectionSelection ===
      PersonalInjuryProtectionSelection.RejectWherePermissible
    ) {
      return 'ShowRejectWherePermissible';
    }
    //Show All Options
    if (
      //PIP-IndividualState
      (this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.IndividualStateCoverage &&
        (this.baseRulesService.additionalOptions ===
          AdditionalOptions.AcceptAdditionalPIP ||
          this.baseRulesService.additionalOptions ===
            AdditionalOptions.VaryByState ||
          this.baseRulesService.additionalOptions ===
            AdditionalOptions.RejectAdditionalPIP)) ||
      //PIP-AcceptCoverage
      (this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.AcceptCoverage &&
        this.baseRulesService.coverageLimit === CoverageLimit.VaryByState)
    ) {
      return 'ShowIndividualAll';
    }

    //No Additional PIP Options
    if (
      (this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.AcceptCoverage &&
        this.baseRulesService.additionalOptions ===
          AdditionalOptions.RejectAdditionalPIP &&
        this.baseRulesService.coverageLimit ===
          CoverageLimit.MinimumLimitsRequired) ||
      (this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.AcceptCoverage &&
        this.baseRulesService.additionalOptions ===
          AdditionalOptions.VaryByState &&
        this.baseRulesService.coverageLimit === CoverageLimit.VaryByState) ||
      (this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.AcceptCoverage &&
        (this.baseRulesService.additionalOptions ===
          AdditionalOptions.RejectAdditionalPIP ||
          this.baseRulesService.additionalOptions ===
            AdditionalOptions.VaryByState) &&
        this.baseRulesService.coverageLimit ===
          CoverageLimit.MinimumLimitsRequired)
    ) {
      return 'ShowBenefitsOptions';
    }
    if (
      this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.AcceptCoverage &&
      this.baseRulesService.coverageLimit ===
        CoverageLimit.MaximumLimitsAvailable
    ) {
      return 'ShowMaxOptions';
    }
    if (
      this.baseRulesService.personalInjuryProtectionSelection ===
        PersonalInjuryProtectionSelection.AcceptCoverage &&
      this.baseRulesService.additionalOptions ===
        AdditionalOptions.AcceptAdditionalPIP &&
      this.baseRulesService.coverageLimit ===
        CoverageLimit.MinimumLimitsRequired
    ) {
      return 'ShowMinOptions';
    }
  }
}
